<!--
This is a sortable, reusable datatable. You use it by passing certain properties in, and use a template
slot to layout each row. Here are the props that the Datatable element uses:

* columns: An array of column names as string. Note: This can be raw HTML, so don't use user input for this.
* entries: The actual list of data elements. This gets passed back into the slot element.
* sortKeys: A list of keys for each sortable element. This should be the same length as the `columns` array and
should have a false-y element in place for any column that isn't sortable. This is required is `sortable` is true.
* sortable: A boolean value indicating whether the table should be sortable at all.
* tableKey: When the table is sort, it fires a `sortTable` event. In order to allow more than one table on a page,
check the `tableKey` when processing the event to ensure you're working with an event from the right table.
* footer: Render the pagination footer? A boolean value.
* perPage: How many items to show per page. Default: no pagination.
* helpText: An array of values used to represent help text in a given column header. This should be the same length as
the number of columns. If a corresponding column doesn't have a help popover, insert a falsy value. This prop is optional.
* separatorColumns: List of column indexes (starting from 1) that should have a right-hand border separator. E.g., [1,3] will
have a separator on the right-border of the first and third columns. This includes the header. Default is [], which is no
separators at all.
* columnCount: If you choose to provide a slot for the header (named "header") instead of a list of column names, you must provide
a column count.
* headerStyle: A list of arbitrary styles to apply to the headers.
* headerClass: A list of arbitrary classes to apply to the headers.

Here is an example of how to use this table element:

```javascript
export default {
  data: function() {
    return {
      entries: [{lastName: 'Smith', firstName: 'Bob', age: 37}, {lastName: 'Smith', firstName: 'Jerry', age: 43}]
      columns: ['Last Name', 'First Name', 'Age']
      sortKeys: [false, 'firstName', 'age']
    }
  },
  methods: {
    sortEntries: function(sortObj) {
      // Sort object looks like: { sortKey: 'firstName', sortDir: 'asc', tableKey: 'myTable' }
      // Do something to sort this.entries with the information in the sortObj.
    }
  }
}
```

```html
<Datatable :sortable="true" :columns="columns" :sort-keys="sortKeys" :table-key="myTable" @sort-table="sortEntries">
  <template slot-scope="props">
    <tr>
      <td>{{ props.entry.lastName }}</td>
      <td>{{ props.entry.firstName }}</td>
      <td>{{ props.entry.age }}</td>
    </tr>
  </template>
</Datatable>
```

or, alternatively, you can provide a slot for the header (if you do so, make sure to provide a column count):

```html
<Datatable :sortable="true" :table-key="mytable" :column-count="5">
  <template slot="header">
    <th>Column 1</th>
    <th>Column 2</th>
    <th>Column 3</th>
  </template>

  <template slot-scope="props">
    <tr>
      <td>{{ props.entry.lastName }}</td>
      <td>{{ props.entry.firstName }}</td>
      <td>{{ props.entry.age }}</td>
    </tr>
  </template>
</Datatable>
```
-->

<template lang="pug">
  .datatable
    table(:style="{width: tableWidth}")
      thead
        slot(name="header")
        //- https://github.com/fezvrasta/popper.js/issues/276
          due to the above bug, popovers won't be correctly positioned when placed inside <th> elements, so i'm using
          <td> elements inside the header. if this bug ever gets corrected, feel free to change this back to <th> and
          make sure to change the <thead> styles below.
        td(v-for="(column, index) in columns"
            :sortIndex="index"
            :sortKey="sortKeys ? sortKeys[index] : 0"
            :class="headerClass"
            :style="headerStyle")
          div.thead-cell(@click.prevent="sortColumn(sortKeys[index])")
            .unclickable(v-html="column")
            .sort-arrow.pl-1(v-if="sortable && sortKeys[index]")
              icon(name="sort" v-if="sortKeys[index] !== currentSortKey")
              icon(name="sort-up" v-if="sortKeys[index] === currentSortKey && sortDir === 'asc'")
              icon(name="sort-down" v-if="sortKeys[index] === currentSortKey && sortDir === 'desc'")
          div.popover-position(v-if="helpText[index]")
              HelpPopover(v-if="helpText[index]",:message="helpText[index]")
      tbody
        template(v-for="(entry, index) in (perPage ? paginatedEntries : entries)")
          slot(:entry="entry" :index="index")
      tfoot(v-if="footer && !printable")
        tr
          td(:colspan="this.numberOfColumns")
            .paginate
              .results
                | Showing results {{ firstEntry + 1 }}-{{ viewAll ? entries.length : paginatedEntries.length + (perPage * (page - 1)) }} of {{ entries.length }}
                span(v-if="entries.length > perPage")
                  a(v-if="!viewAll",href="",@click.prevent="viewAll = true",class="ml-2") View All
                  a(v-if="viewAll",href="",@click.prevent="viewAll = false",class="ml-2") Hide All
              .buttons.form-inline
                button.btn.btn-light(@click.prevent="page = parseInt(page) - 1",:disabled="page <= 1") Previous
                input.form-control(type="test",:value="viewAll ? 1 : page",@input="page = $event.target.value")
                |  of {{ totalPages }}
                button.btn.btn-light(@click.prevent="page = parseInt(page) + 1",:disabled="page >= totalPages") Next
</template>

<script>
import HelpPopover from './HelpPopover'
import 'vue-awesome/icons/sort'
import 'vue-awesome/icons/sort-up'
import 'vue-awesome/icons/sort-down'
export default {
  name: 'Datatable',
  props: {
    columns: {
      type: Array,
      default: () => [],
    },
    entries: {
      type: Array,
      default: () => [],
    },
    sortKeys: {
      type: Array,
      default: () => [],
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    tableKey: {
      type: String,
      default: 'datatable',
    },
    perPage: {
      type: Number,
      default: null,
    },
    footer: {
      type: Boolean,
      default: true,
    },
    helpText: {
      type: Array,
      default: () => [],
    },
    separatorColumns: {
      type: Array,
      default: () => [],
    },
    columnCount: {
      type: Number,
      default: 1,
    },
    headerStyle: {
      type: [Object, String],
      default: () => {},
    },
    headerClass: {
      type: [Object, String],
      default: 'h6',
    },
    tableWidth: {
      type: String,
      default: '100%',
    },
  },
  data: function() {
    return {
      page: 1,
      sortDir: 'none',
      currentSortKey: undefined,
      viewAllHidden: false,
    }
  },
  methods: {
    sortColumn: function(sortKey) {
      if (this.sortable) {
        let currentSortKey = sortKey
        // If this isn't a sortable column, stop and don't change the sort. Otherwise, sort away
        if (this.sortKeys.indexOf(currentSortKey) < 0) return
        this.currentSortKey = currentSortKey
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'

        this.$emit('sortTable', {
          sortKey: this.currentSortKey,
          sortDir: this.sortDir,
          tableKey: this.tableKey,
        })
      }
    },
  },
  computed: {
    sortAsc: function() {
      return this.sortDir === 'asc'
    },
    sortDesc: function() {
      return this.sortDir === 'desc'
    },
    totalPages: function() {
      return this.viewAll ? 1 : Math.ceil(this.entries.length / this.perPage)
    },
    paginatedEntries: function() {
      return this.viewAll
        ? this.entries
        : this.entries.slice(this.firstEntry, this.lastEntry)
    },
    firstEntry: function() {
      return Math.max(
        (Math.min(this.page, this.totalPages) - 1) * this.perPage,
        0,
      )
    },
    lastEntry: function() {
      // return this.viewAll ? this.entries.length : this.paginatedEntries.length + (this.perPage * (page - 1))
      return this.firstEntry + this.perPage
    },
    numberOfColumns: function() {
      return this.columns.length > 0 ? this.columns.length : this.columnCount
    },
    viewAll: {
      get() {
        return this.viewAllHidden || this.printable
      },
      set(newValue) {
        this.viewAllHidden = newValue
      },
    },
  },
  components: {
    HelpPopover,
  },
  updated: function() {
    this.separatorColumns.forEach(col =>
      document.body
        .querySelectorAll(`td:nth-child(${col})`)
        .forEach(el => el.classList.add(this.$style.border)),
    )
  },
}
</script>

<style lang='scss' scoped>
@import '../assets/styles/bodies.scss';
@import '../assets/styles/breakpoints';
@import '../assets/styles/headers';
$cell-padding: 0.8rem;

.datatable {
  @media (min-width: 992px) {
    border: 1px solid #d8d8d8;
  }
  // display: flex;
  flex-direction: column;
  overflow-x: auto;
}
table {
  min-width: 100%;
  thead {
    background-color: #ededed;
    td,
    th {
      padding: $cell-padding;
      vertical-align: top;

      .unclickable {
        pointer-events: none;
        user-select: none;
        flex-shrink: 1;
        word-wrap: break-word;
      }
    }

    td:not(:last-child),
    th:not(:last-child) {
      border-right: 1px solid #d8d8d8;
    }
  }

  tbody {
    td {
      padding: $cell-padding;
      vertical-align: top;
    }
    td:not(:last-child) {
      border-right: 1px solid #d8d8d8;
    }

    tr:nth-child(2n) {
      background-color: #f8f8f8;
    }
  }

  tfoot {
    tr {
      td {
        padding: 0;
      }
    }
  }
}

.paginate {
  @extend .body-2;
  padding: $cell-padding;
  background-color: #ededed;

  display: flex;
  flex-direction: row;

  .results {
    flex-grow: 1;
  }

  .buttons {
    flex-grow: 3;
    * {
      margin-left: 0.4rem;
      margin-right: 0.4rem;
    }
    input {
      width: 3rem;
    }
  }
}

.sort-arrow {
  float: right;
}

@media (max-width: $bp-lg) {
  table {
    overflow-x: scroll;
  }
}

.popover-position {
  position: absolute;
  bottom: 0;
  padding-bottom: 0.3rem;
}

.thead-cell {
  display: flex;
}

// workaround for browser bug: https://bugzilla.mozilla.org/show_bug.cgi?id=409254
tbody:empty {
  display: none;
}
</style>

<style lang='scss' module>
.border {
  border-right: 2px solid darken(#d8d8d8, 20%) !important;
}
</style>
